import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "@components/layout";
import privateRoutes from "@routes/private";
import Login from "views/Login";
import Dashboard from "views/Dashboard";
import AdminSettings from "views/AdminSettings";
import ChangePassword from "views/AdminSettings/ChangePassword";
import { isAdmin, isEmpty, readFromLocalStorage } from "utils/helper";
import SC, { GlobalStyles } from "./style";
import ProjectsLevel from "views/AdminSettings/Projects/ProjectLevel";
import ProjectGeneralInformartion from "views/ProjectHome/ProjectGeneralInformartion";
import ProjectHome from "views/ProjectHome";
import Occupant from "views/AdminSettings/Projects/Occupant";
import OccupantHomePage from "views/ProjectHome/ProjectGeneralInformartion/OccupantHomePage";
import FolderPermissionAndStructure from "views/AdminSettings/FolderTemplate/FolderPermissionAndStructure";
import FileDetailsView from "views/ProjectHome/ListFileAndPermission/FileDetailsView";
import ListFileAndPermission from "views/ProjectHome/ListFileAndPermission";
import AddressBook from "views/ProjectHome/AddressBook";
import Journal from "views/ProjectHome/Journal";
import AddEditJournal from "views/ProjectHome/Journal/AddEditJournal";
import { useDispatch } from "react-redux";
import { selectRoles } from "@store/userRoles/actions";
import SessionTimeout from "components/SessionTimeout";
import { getRoleOptions } from "@store/roleOptions/actions";
import OcuupantInfo from "views/ProjectHome/OcuupantInfo";
import MyCart from "views/ProjectHome/MyCart";
import { getCart } from "@store/cart/actions";
import DownloadCart from "views/DownLoadCart";
import UpdateJournal from "views/ProjectHome/Journal/UpdateJournal";
import OpenThread from "views/ProjectHome/Journal/OpenThread";
import PageNotFound from "components/PageNotFound";
import BulkJournal from "views/ProjectHome/Journal/BulkJournal";
import ExportProject from "InnoUI/components/exportProject";
import { JournalDetailsView } from "@components/";
import ManageFolderTemplate from "components/ManageFolderTemplate";
import DocumentSearch from "views/ProjectHome/DocumentSearch";
import ProjectInformation from "views/ProjectHome/ProjectInformation";
import { getAccessControl } from "@store/accessControl/actions";
import { selectOccupantOption } from "@store/occupantOption/actions";
import AdvancedSearch from "views/ProjectHome/AdvancedSearch";
import { auth, LogoutAction } from "@store/auth/actions";
import { authUserSuccess } from "@store/auth/slice";
import UpdatePrompts from "views/AdminSettings/SystemPrompts/UpdatePrompts";
import Photos from "views/ProjectHome/Photos";
import CheckPhotosPermission from "views/ProjectHome/Photos/CheckPhotosPermission";
import MultiUpload from "views/ProjectHome/Photos/MultiUpload";
import CatalogDetails from "views/ProjectHome/Photos/CatalogDetails";
import { fetchSIngleProjectAction } from "@store/project/actions";
import { fetchOccupantAction } from "@store/occupant/actions";
import { projectType } from "utils/lookUpTable";

const AppRoutes = () => {
  const adminDrawer = privateRoutes?.adminDrawer || [];
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin-settings");
  const accessLevel = useSelector(
    (state) => state?.authdata?.data?.userType || 0
  );

  const dispatch = useDispatch();

  const currentProject = useSelector(
    (state) => state?.currentProject?.data || null
  );
  const currentOccupant = useSelector(
    (state) => state?.currentOccupant?.data || null
  );

  const authData = useSelector((state) => state?.authdata?.data || null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const routeProjectId = searchParams.get("projectId");
    const routeType = searchParams.get("type");

    if (isEmpty(routeProjectId)) return;

    if (routeType === String(projectType.occupant)) {
      if (String(currentOccupant?.id || "") !== String(routeProjectId)) {
        dispatch(fetchOccupantAction(routeProjectId));
      }
      return;
    }

    if (String(currentProject?.id || "") !== String(routeProjectId)) {
      dispatch(fetchSIngleProjectAction(routeProjectId));
    }
  }, [
    currentOccupant?.id,
    currentProject?.id,
    dispatch,
    location.search,
  ]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const routeType = searchParams.get("type");
    const parentProjectId = currentOccupant?.projectId;

    if (
      routeType === String(projectType.occupant) &&
      parentProjectId &&
      String(currentProject?.id || "") !== String(parentProjectId)
    ) {
      dispatch(fetchSIngleProjectAction(parentProjectId));
    }
  }, [currentOccupant?.projectId, currentProject?.id, dispatch, location.search]);

  useEffect(() => {
    if (authData && currentProject) {
      const newData = {
        projectId: currentProject?.id,
        userId: authData?.id,
      };
      dispatch(getCart({ id: currentProject?.id }));
      dispatch(selectRoles(newData));
      dispatch(getRoleOptions());
    }
  }, [currentProject]);

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(getAccessControl(currentProject.id));
      dispatch(selectOccupantOption(currentProject.id));
    }
  }, [currentProject?.id]);

  //logout and login-event
  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === "logout-event") {
        // Handle logout
        dispatch(LogoutAction({ broadcast: false }));
      } else if (event.key === "login-event") {
        // Handle login
        const userData = JSON.parse(readFromLocalStorage("user"));
        if (userData) {
          dispatch(auth(userData));
        }
      }
    };
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, []);

  const AdminRoutes = [
    { path: "/admin-settings/projects/level", element: <ProjectsLevel /> },
    { path: "/admin-settings/projects/occupant", element: <Occupant /> },
    {
      path: "/admin-settings/folder-template/permission-and-structure",
      element: <FolderPermissionAndStructure />,
    },
    { path: "/admin-settings", element: <AdminSettings /> },
    {
      path: "/admin-settings/system-prompts/update-prompts",
      element: <UpdatePrompts />,
    },
  ];

  const DefaultRoutes = [
    {
      path: "/change-password",
      element: <ChangePassword />,
      showdrawer: false,
    },
    { path: "/", element: <Dashboard />, showdrawer: false },
    // {
    //   path: "/downloadproject",
    //   element: <DownloadProject />,
    //   showdrawer: false,
    // },
    { path: "/project-home", element: <ProjectHome /> },
    {
      path: "/project-home/generalinformation",
      element: <ProjectGeneralInformartion defaultTab={0} />,
    },
    {
      path: "/project-home/project-information",
      element: <ProjectInformation />,
    },
    {
      path: "/project-home/levels",
      element: <ProjectGeneralInformartion defaultTab={1} />,
    },
    { path: "/project-home/occupanthomepage", element: <OccupantHomePage /> },
    { path: "/project-home/documentsearch", element: <DocumentSearch /> },
    { path: "/project-home/advancedsearch", element: <AdvancedSearch /> },
    { path: "/journal", element: <Journal /> },
    { path: "/journal/add-journal", element: <AddEditJournal /> },
    { path: "/photos", element: <CheckPhotosPermission /> },
    { path: "/photos/multiupload", element: <MultiUpload /> },
    { path: "/photos/catalogview", element: <CatalogDetails /> },

    {
      path: "/journal-details",
      element: <JournalDetailsView />,
      showdrawer: false,
    },
    { path: "/journal/update-journal", element: <UpdateJournal /> },
    { path: "/journal/open-thread", element: <OpenThread /> },
    { path: "/document", element: <ListFileAndPermission /> },
    { path: "/address-book", element: <AddressBook /> },
    { path: "/document/file-details", element: <FileDetailsView /> },
    { path: "/occupant-info", element: <OcuupantInfo /> },
    { path: "/cart", element: <MyCart /> },
    { path: "/bulk-journal", element: <BulkJournal /> },
    {
      path: "/folder-template/permission-and-structure",
      element: <FolderPermissionAndStructure />,
    },
    {
      path: "/manage-template",
      element: <ManageFolderTemplate />,
    },
  ];

  return (
    <Routes>
      {/* Download Cart */}
      <Route
        path="/downloadCart"
        element={
          <Layout showdrawer={false} drawerItems={[]} drawerVariant="admin">
            <DownloadCart />
          </Layout>
        }
      />
      {/* <Route
        path="/downloadproject"
        element={
          <Layout showdrawer={false} drawerItems={[]} drawerVariant="admin">
            <DownloadProject />
          </Layout>
        }
      /> */}
      {/* <Route
        path="/exportproject"
        element={
          <Layout showdrawer={false} drawerItems={[]} drawerVariant="admin">
            <ExportProject />
          </Layout>
        }
      /> */}
      {/* Admin Layout Routes */}
      {isAdmin(accessLevel) && isAdminRoute ? (
        <>
          {AdminRoutes.map(({ path, element }) => (
            <Route
              key={path}
              path={path}
              element={
                <Layout
                  showdrawer={true}
                  drawerItems={adminDrawer}
                  drawerVariant="admin"
                >
                  {element}
                </Layout>
              }
            />
          ))}
          {adminDrawer.map((item) => (
            <Route
              key={item.route}
              path={item.route}
              element={
                <Layout
                  showdrawer={true}
                  drawerItems={adminDrawer}
                  drawerVariant="admin"
                >
                  {item.element}
                </Layout>
              }
            />
          ))}
        </>
      ) : (
        <>
          {DefaultRoutes.map(({ path, element, showdrawer = true }) => (
            <Route
              key={path}
              path={path}
              element={
                <Layout
                  showdrawer={showdrawer}
                  drawerItems={[]}
                  drawerVariant="default"
                >
                  {element}
                </Layout>
              }
            />
          ))}
        </>
      )}
      {/* Not Found Route */}
      <Route
        path="*"
        element={
          <Layout showdrawer={false} drawerItems={[]} drawerVariant="default">
            <PageNotFound />
          </Layout>
        }
      />
    </Routes>
  );
};

function App() {
  const { isAuthenticated } = useSelector((state) => state.authdata);

  return (
    <>
      {/* <GlobalStyles />
      <Router>
        <Routes>
          <Route path="/exportproject" element={<ExportProject />} />
        </Routes>
        <div className="App">
          {isAuthenticated ? (
            <>
              <SessionTimeout />
              <AppRoutes />
            </>
          ) : (
            <Login />
          )}
        </div>
      </Router> */}

      <GlobalStyles />

      <Router>
        <div className="App">
          {isAuthenticated && <SessionTimeout />}
          <Routes>
            {/* ✅ Public Route: ExportProject */}
            <Route path="/exportproject" element={<ExportProject />} />

            {/* ✅ Authenticated Routes */}
            {isAuthenticated ? (
              <Route path="*" element={<AppRoutes />} />
            ) : (
              // ✅ Fallback Login for everything else
              <Route path="*" element={<Login />} />
            )}
          </Routes>
        </div>
      </Router>
    </>
  );
}

export default App;
